import React, { lazy, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import AppRoute from "./route";
import Layout from "../container/layout";
import { connect } from "react-redux";
import { getStoredAuth, clearStoredAuth } from "../utils/authStorage";
import { checkSession } from "../store/auth/apis";
import { updateAuthData, updateExam } from "../store/auth/actions";
import { SYSTEM_ID_CHANGED, getStoredSystemId, refreshSystemId } from "../utils/systemId";
import { start as startJournal } from "../utils/journal";
import { notification } from "../components/loader";
// OTHER_MACHINE: 905 (logged in on another system), 907 (exam locked to the system of the first login). Such a
// login cannot be used here, so it ends (after a picture), and the login page gives the reason.
import { OTHER_MACHINE, photographLogout } from "../utils/axios";

// The machine's ID is read again every 15 minutes during the exam, and every minute while the registration app
// does not answer. Each read runs PowerShell on the centre computer (the registration app reads the BIOS serial
// afresh every time), and centre antivirus (360 Total Security) blocked it on 12 Oct 2026 after many reads.
const SYSTEM_CHECK_MS = 15 * 60 * 1000;
const SYSTEM_RETRY_MS = 60 * 1000;

const QuestionPage = lazy(() => import("../pages/questionPage"))
const InstructionPage = lazy(() => import("../pages/instructionPage"))
const DashboardPage = lazy(() => import("../pages/dashboardPage"))
const FinalPage = lazy(() => import("../pages/finalPage"))
const LoginPage = lazy(() => import("../pages/loginPage"))
const FeedBackPage = lazy(() => import("../pages/feedbackPage"))
const ResultPage = lazy(() => import("../pages/resultPage"))
const ResultTablePage = lazy(() => import("../pages/resultTablePage"))


const openRoutes = [
    { path: "/", exact: true, component: LoginPage }
];

const userRoutes = [
    { path: "/question/:id", exact: true, component: QuestionPage },
    { path: "/instruction", exact: true, component: InstructionPage },
    { path: "/dashboard", exact: true, component: DashboardPage },
    { path: "/final_page", exact: true, component: FinalPage },
    { path: "/feedback", exact: true, component: FeedBackPage },
    // { path: "/result", exact: true, component: ResultPage },
    { path: "/result-details", exact: true, component: ResultTablePage }
];


const PagesRoute = (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    // Read from storage as well as the store: the store is filled in an effect, after the first render,
    // and a page opened by URL has to be judged before anything of it renders.
    const token = props.auth.auth?.token || getStoredAuth()?.token;
    const atLogin = location.pathname === "/";

    // A page opened with a stored login (typed URL, reload) is shown only once the server confirms the
    // login is still live. Moving between pages after logging in needs no check: no reload happens.
    const [verified, setVerified] = useState(atLogin || !token);

    // The candidate's journal: page load, offline / hidden periods, and the upload of any backlog
    useEffect(() => {
        startJournal();
    }, []);

    useEffect(() => {
        if (verified) return;
        // This runs before App's effect that loads the stored login, and the API helper reads the token
        // from the store, so put it there first
        if (!props.auth.auth) props.updateAuthData(getStoredAuth());
        // The machine's ID is read again on every page load, not reused from the login page: the session and
        // the exam are tied to one machine, and the server can only tell if each request says which one
        refreshSystemId()
            .then(() => checkSession())
            .then(async (res) => {
                // Only an ended session (901) logs the candidate out. Anything else (a server restart, a proxy
                // error, a check that failed) keeps the login: the exam API still checks every request, and
                // logging out an entered candidate would need a centre re-login to get them back.
                if (!res.status && OTHER_MACHINE.includes(res.errorCode)) {
                    notification(false, res.message);
                    await photographLogout(res.errorCode, res.message);
                }
                if (!res.status && (res.errorCode === 901 || OTHER_MACHINE.includes(res.errorCode))) {
                    clearStoredAuth();
                    props.updateAuthData(null);
                    props.updateExam({});
                    navigate("/", { replace: true });
                }
                setVerified(true);
            })
            // Server unreachable: show the page; every exam API call is still checked by the server
            .catch(() => setVerified(true));
        // Once per page load: navigation after login does not reload, so it needs no check
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ... and again every 15 minutes during the exam, live: a registration app that doesn't answer leaves no ID, so
    // the candidate's requests are refused until it answers again, which is then checked every minute
    const loggedIn = !!token && !atLogin;
    useEffect(() => {
        if (!loggedIn) return undefined;
        let timer;
        let stopped = false;
        const schedule = (ms) => {
            clearTimeout(timer);
            if (!stopped) timer = setTimeout(check, ms);
        };
        const check = async () => {
            const id = await refreshSystemId();
            schedule(id ? SYSTEM_CHECK_MS : SYSTEM_RETRY_MS);
        };
        // Whenever a read gets no answer (this page load's too), the next one is the shorter wait away
        const onChange = () => {
            if (!getStoredSystemId()) schedule(SYSTEM_RETRY_MS);
        };
        window.addEventListener(SYSTEM_ID_CHANGED, onChange);
        schedule(getStoredSystemId() ? SYSTEM_CHECK_MS : SYSTEM_RETRY_MS);
        return () => {
            stopped = true;
            clearTimeout(timer);
            window.removeEventListener(SYSTEM_ID_CHANGED, onChange);
        };
    }, [loggedIn]);

    if (!token && !atLogin) return <Navigate to="/" replace />;

    const routes = token ? [...openRoutes, ...userRoutes] : openRoutes;
    return (
        <Layout>
            {verified && routes.map((route, idx) => (
                <AppRoute path={route.path} component={route.component} key={idx} isAuthProtected={true} />
            ))}
        </Layout>
    );
};

const mapStateToProps = (state) => {
    return {
        auth: state.Auth,
    }
}

const mapDispatchToProps = { updateAuthData, updateExam }

export default connect(mapStateToProps, mapDispatchToProps)(PagesRoute);